import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate, Link } from 'react-router-dom';

import FounderTypeConstellation from '../components/FounderTypeConstellation';
import DnaBackground from '../components/DnaBackground';
import DnaLoadingAnimation from '../components/DnaLoadingAnimation';
import QuestionCard from '../components/QuestionCard';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';
import Seo from '../components/Seo';
import StorageBlockedNotice from '../components/StorageBlockedNotice';
import { trackEvent } from '../utils/analytics';
import { isLocalStorageAvailable } from '../utils/storageAvailability';

import { questions } from '../data/questions';
import { calculateProfile, saveResults, getResults } from '../utils/profileCalculator';
import { LANDING } from '../data/productCopy';

const QuizContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 2.5rem 1.25rem;
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 680px;
  margin: 0 auto;
`;

const IntroContent = styled(motion.div)`
  text-align: center;
  width: 100%;
  max-width: 560px;
  margin: 0 auto 2rem;
  padding: 2rem 1.5rem 1.75rem;
  border-radius: 20px;
  background: rgba(18, 18, 28, 0.82);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(12px);
`;

const IntroTitle = styled.h1`
  font-family: var(--font-display);
  font-size: 2.25rem;
  margin-bottom: 1rem;
  color: var(--text-primary);
  font-weight: 800;
  letter-spacing: -0.03em;

  @media (min-width: 768px) {
    font-size: 2.6rem;
  }
`;

const IntroDescription = styled.p`
  font-size: 1.05rem;
  color: var(--text-secondary);
  margin-bottom: 2rem;
  line-height: 1.6;
  max-width: 540px;

  @media (min-width: 768px) {
    font-size: 1.15rem;
  }
`;

const DisclaimerText = styled.p`
  color: rgba(255, 255, 255, 0.65);
  font-size: 0.95rem;
  line-height: 1.5;
  margin-top: 1rem;
`;

const StartButton = styled(motion.button)`
  background: var(--primary-color);
  color: #0C0F14;
  font-weight: 600;
  font-size: 1.1rem;
  padding: 1rem 2rem;
  border-radius: var(--border-radius);
  cursor: pointer;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  letter-spacing: 0.5px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(var(--primary-rgb), 0.4);
  }

  &:focus-visible {
    outline: 2px solid rgba(var(--primary-rgb), 0.65);
    outline-offset: 3px;
  }
`;

const ContinueCard = styled.div`
  width: 100%;
  margin-top: 1.25rem;
  padding: 1.25rem 1.25rem 1.1rem;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.22);
  backdrop-filter: blur(10px);
`;

const ContinueTitle = styled.h2`
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 0.4rem;
`;

const ContinueText = styled.p`
  margin: 0 0 1rem;
  color: rgba(255, 255, 255, 0.75);
  line-height: 1.5;
  font-size: 0.98rem;
`;

const ButtonStack = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;
  width: 100%;
  max-width: 420px;
  margin: 0 auto;
`;

const SecondaryButton = styled(motion.button)`
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.14);
  color: white;
  font-weight: 600;
  font-size: 1.05rem;
  padding: 0.95rem 1.25rem;
  border-radius: var(--border-radius);
  cursor: pointer;
  letter-spacing: 0.3px;

  &:hover {
    transform: translateY(-2px);
    background: rgba(255, 255, 255, 0.11);
    box-shadow: 0 10px 28px rgba(0, 0, 0, 0.22);
  }

  &:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.55);
    outline-offset: 3px;
  }
`;

const Quiz = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [userAnswers, setUserAnswers] = useState([]);
  const [showIntro, setShowIntro] = useState(true);
  const [isCalculating, setIsCalculating] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [profilePreview, setProfilePreview] = useState(null);
  const [savedResultsMeta, setSavedResultsMeta] = useState(() => ({
    hasResults: false,
    createdAt: null,
  }));
  const storageAvailable = isLocalStorageAvailable();
  const [storageError, setStorageError] = useState('');
  
  // Handle answer selection and move to next question
  const handleNextQuestion = (optionOverride) => {
    const option = optionOverride || selectedOption;
    if (!option) return;
    
    // Save the current answer
    const currentQuestion = questions[currentQuestionIndex];
    const answerEvidence = {
      questionId: currentQuestion?.id,
      questionText: currentQuestion?.text,
      optionId: option?.id,
      optionText: option?.text,
      type: option?.type,
    };

    const newAnswers = [...userAnswers, answerEvidence];
    setUserAnswers(newAnswers);
    
    // Move to next question or finish
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prevIndex => prevIndex + 1);
      setSelectedOption(null);
    } else {
      const completedAnswers = newAnswers;
      const preview = calculateProfile(completedAnswers);
      setProfilePreview(preview);
      setIsCalculating(true);
      setAnalysisProgress(0);

      setTimeout(() => {
        try {
          saveResults(preview, completedAnswers);
          setStorageError('');
        } catch (e) {
          setStorageError('Could not save results on this device. Check browser storage settings.');
        }

        trackEvent('quiz_completed', {
          totalQuestions: questions.length,
          primaryType: preview?.primaryType || null,
          secondaryType: preview?.secondaryType || null,
        });
        
        navigate('/results/reveal');
      }, 3500);
    }
  };
  
  const handleBackQuestion = () => {
    if (currentQuestionIndex === 0) {
      setShowIntro(true);
      setUserAnswers([]);
      setSelectedOption(null);
      return;
    }
    const prevIndex = currentQuestionIndex - 1;
    const prevAnswer = userAnswers[prevIndex];
    setUserAnswers(userAnswers.slice(0, prevIndex));
    setCurrentQuestionIndex(prevIndex);
    const q = questions[prevIndex];
    setSelectedOption(
      prevAnswer ? q.options.find((o) => o.id === prevAnswer.optionId) || null : null
    );
  };

  // Handle start quiz
  const handleStartQuiz = (method = 'intro_button') => {
    trackEvent('quiz_started', { method });
    setProfilePreview(null);
    setShowIntro(false);
  };

  const handleContinueToResults = () => {
    trackEvent('returning_user_continue', { destination: 'results' });
    navigate('/results');
  };

  const handleContinueToCheckIn = () => {
    trackEvent('returning_user_continue', { destination: 'check_in' });
    navigate('/check-in');
  };

  // Detect returning users with saved results
  useEffect(() => {
    const resultsData = getResults();
    const hasResults = Boolean(resultsData?.profile?.primaryType);
    setSavedResultsMeta({
      hasResults,
      createdAt: resultsData?.createdAt || null,
    });
  }, []);
  
  // Reset scroll on question change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentQuestionIndex]);

  // Drive the "analyzing" progress bar for a smoother UX
  useEffect(() => {
    if (!isCalculating) return;

    const durationMs = 3500;
    const startedAt = Date.now();
    const tickMs = 50;

    const interval = setInterval(() => {
      const elapsed = Date.now() - startedAt;
      const pct = Math.min(100, Math.round((elapsed / durationMs) * 100));
      setAnalysisProgress(pct);
      if (pct >= 100) clearInterval(interval);
    }, tickMs);

    return () => clearInterval(interval);
  }, [isCalculating]);
  
  return (
    <>
      <Seo
        title={LANDING.title}
        description={LANDING.metaDescription}
        path={location.pathname}
        imagePath="/favicon.svg"
      />
      <DnaBackground fullWidth />
      <AnimatePresence>
        {showIntro && (
          <motion.div
            key="constellation"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.45 }}
            style={{ position: 'fixed', inset: 0, zIndex: 1, pointerEvents: 'none' }}
          >
            <FounderTypeConstellation />
          </motion.div>
        )}
      </AnimatePresence>
      <QuizContainer id="main-content">
        <SiteHeader showCta={false} />
        {!storageAvailable && <StorageBlockedNotice maxWidth="560px" />}
        {storageError && (
          <DisclaimerText role="alert" style={{ color: '#fde68a', maxWidth: '560px', width: '100%' }}>
            {storageError}
          </DisclaimerText>
        )}
        
        <AnimatePresence mode="wait">
          {showIntro ? (
            <IntroContent
              key="intro"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
            >
              <IntroTitle>{LANDING.title}</IntroTitle>
              <IntroDescription>{LANDING.description}</IntroDescription>
              <DisclaimerText>{LANDING.disclaimer}</DisclaimerText>
              {savedResultsMeta.hasResults ? (
                <ContinueCard>
                  <ContinueTitle>Welcome back</ContinueTitle>
                  <ContinueText>
                    Your founder type is saved on this device
                    {savedResultsMeta.createdAt ? ` (updated ${new Date(savedResultsMeta.createdAt).toLocaleDateString()}).` : '.'}
                  </ContinueText>
                  <ButtonStack>
                    <StartButton
                      onClick={handleContinueToResults}
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.98 }}
                      aria-label="View saved results"
                    >
                      View my results
                    </StartButton>
                    <SecondaryButton
                      onClick={() => handleStartQuiz('retake_button')}
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.98 }}
                      aria-label="Retake the test"
                    >
                      Retake the test
                    </SecondaryButton>
                  </ButtonStack>
                  <DisclaimerText style={{ marginTop: '1rem', marginBottom: 0 }}>
                    <button
                      type="button"
                      onClick={handleContinueToCheckIn}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--primary-light)',
                        cursor: 'pointer',
                        font: 'inherit',
                        padding: 0,
                        textDecoration: 'underline',
                        textUnderlineOffset: '3px',
                      }}
                    >
                      Weekly check-in →
                    </button>
                  </DisclaimerText>
                </ContinueCard>
              ) : (
                <StartButton
                  onClick={() => handleStartQuiz('intro_button')}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Take the test
                </StartButton>
              )}
              <DisclaimerText style={{ marginTop: '1.25rem' }}>
                <Link to="/types" style={{ color: 'var(--primary-light)' }}>Explore the 6 founder types</Link> before you start
              </DisclaimerText>
            </IntroContent>
          ) : isCalculating ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <DnaLoadingAnimation
                title="Calculating your founder type"
                subtitle="We are scoring your answers across six founder modes and building your identity profile."
                progress={analysisProgress}
                profilePreview={profilePreview}
                steps={[
                  'Reading your decision patterns',
                  'Scoring weighted founder modes',
                  'Identifying your primary type',
                  'Preparing your reveal and playbook',
                ]}
              />
            </motion.div>
          ) : (
            <motion.div
              key="question"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              style={{ width: '100%' }}
            >
              <QuestionCard
                question={questions[currentQuestionIndex]}
                currentIndex={currentQuestionIndex}
                totalQuestions={questions.length}
                selectedOption={selectedOption}
                setSelectedOption={setSelectedOption}
                onNext={handleNextQuestion}
                onBack={handleBackQuestion}
              />
            </motion.div>
          )}
        </AnimatePresence>
        <SiteFooter compact />
      </QuizContainer>
    </>
  );
};

export default Quiz;